import { PATH } from '#const/path';
import { coachMarkState } from '#states/atoms/coachMarkState';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';

// Define the possible paths for coach marks
const initialStatePaths = {
  [PATH.CALENDER_DETAIL]: true,
  '/calender/schedule-registration': true,
  [PATH.DASHBOARD_CUSTOMER]: true,
  [PATH.HOME]: true
} as const;

type InitialStatePaths = typeof initialStatePaths;
type PathKey = keyof InitialStatePaths;

export const useCoachMark = () => {
  const { pathname } = useLocation();
  const [coachMarks, setCoachMarks] = useRecoilState(coachMarkState);

  // Function to find if pathname contains any key from initialStatePaths
  const getMatchingCoachMarkPath = (path: string): PathKey | undefined => {
    return Object.keys(initialStatePaths).find(key => path.includes(key)) as
      | PathKey
      | undefined;
  };

  const matchingPath = getMatchingCoachMarkPath(pathname);

  useEffect(() => {
    if (matchingPath !== undefined) {
      // Check local storage for the current path
      const localStorageKey = `coachMark-${matchingPath}`;
      const storedValue = localStorage.getItem(localStorageKey);

      if (storedValue === null) {
        // If no value in local storage, set default to true
        localStorage.setItem(localStorageKey, 'true');
        setCoachMarks(prev => ({ ...prev, [matchingPath]: true }));
      } else {
        // Use the stored value
        setCoachMarks(prev => ({
          ...prev,
          [matchingPath]: storedValue === 'true'
        }));
      }
    } else {
      // If no matching path found, ensure isCoachMark is false
      setCoachMarks(prev => ({ ...prev, [pathname]: false }));
    }
  }, [pathname, matchingPath, setCoachMarks]);

  return {
    isCoachMark: matchingPath ? coachMarks[matchingPath] || false : false,
    setCoachMark: (value: boolean) => {
      if (matchingPath !== undefined) {
        const localStorageKey = `coachMark-${matchingPath}`;
        localStorage.setItem(localStorageKey, value.toString());
        setCoachMarks(prev => ({ ...prev, [matchingPath]: value }));
      }
    }
  };
};
